import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    useTranslate,
    useRedirect,
    useNotify,
    useRecordContext,
    Toolbar,
    SaveButton,
    maxLength,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Alert,
} from '@mui/material';
import { ArrowBack, EditNote } from '@mui/icons-material';
import { ReservationStatusChip } from './ReservationsList';

const EditToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
        }}
    >
        <SaveButton variant="contained" />
    </Toolbar>
);

const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
            <IconButton
                onClick={() => redirect('/reservations')}
                size="large"
                sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                title={translate('resources.reservations.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.reservations.edit.title')}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }} color="text.secondary">
                    {translate('resources.reservations.edit.subtitle')}
                </Typography>
            </Box>
            {record?.status && <ReservationStatusChip status={record.status} />}
        </Box>
    );
};

export const ReservationsEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();

    // Whitelist: only admin_notes and cancelled_reason are user-editable
    const transform = (data: any) => {
        return {
            admin_notes: data.admin_notes ?? data.adminNotes ?? null,
            cancelled_reason: data.cancelled_reason ?? data.cancelledReason ?? null,
        };
    };

    return (
        <Edit
            title={false}
            component="div"
            mutationMode="pessimistic"
            transform={transform}
            mutationOptions={{
                onSuccess: () => {
                    notify('resources.reservations.notifications.updated', { type: 'success' });
                    redirect('list', 'reservations');
                },
                onError: () => {
                    notify('resources.reservations.notifications.error.update', { type: 'error' });
                },
            }}
        >
            <SimpleForm toolbar={<EditToolbar />}>
                <Box sx={{ width: '100%', maxWidth: 900, margin: '0 auto', pb: { xs: 10, sm: 2 } }}>
                    <PageHeader />
                    <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                        {translate('resources.reservations.edit.statusChangeWarning')}
                    </Alert>
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <EditNote sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.reservations.show.sections.modifications')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="admin_notes"
                                    label={translate('resources.reservations.edit.adminNotes')}
                                    multiline
                                    rows={4}
                                    fullWidth
                                    validate={[maxLength(2000)]}
                                />
                                <TextInput
                                    source="cancelled_reason"
                                    label={translate('resources.reservations.edit.cancellationReason')}
                                    multiline
                                    rows={3}
                                    fullWidth
                                    validate={[maxLength(1000)]}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};

export default ReservationsEdit;
